import { auth } from '../config/firebase';

export const OWNER_EMAIL = 'argamtaha2009@gmail.com';

const PUBLIC_ORIGIN = process.env.REACT_APP_PUBLIC_APP_URL || 'https://fleet-fix-alpha.vercel.app';

export const isOwnerEmail = (email?: string | null): boolean =>
  Boolean(email && email.trim().toLowerCase() === OWNER_EMAIL);

const getApiUrl = (path: string, configuredUrl?: string): string => {
  if (configuredUrl?.trim()) return configuredUrl.trim();
  if (typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname)) {
    return `${PUBLIC_ORIGIN.replace(/\/+$/, '')}${path}`;
  }
  return path;
};

const formatApiMessage = (value: unknown, fallback: string): string => {
  if (!value) return fallback;
  if (typeof value === 'string') return value;
  if (value instanceof Error) return value.message || fallback;
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const nested = record.message || record.error || record.setup || record.detail || record.reason;
    if (typeof nested === 'string' && nested.trim()) return nested;
    try {
      return JSON.stringify(value);
    } catch {
      return fallback;
    }
  }
  return String(value);
};

const getApiErrorMessage = (result: any, fallback: string): string =>
  formatApiMessage(result?.error || result?.setup || result?.message, fallback);

export interface FeedbackPayload {
  category: string;
  subject: string;
  message: string;
  senderName?: string;
  senderEmail?: string;
  pageUrl?: string;
  accountId?: string;
  companyId?: string;
}

export interface BroadcastRecipient {
  uid?: string;
  email: string;
  name?: string;
  role?: string;
  status?: string;
  emailVerified?: boolean;
  disabled?: boolean;
  providerIds?: string[];
  phoneNumber?: string;
  createdAt?: string;
  lastSignInAt?: string;
}

export interface AdminRegisteredUserProfile {
  id?: string;
  email: string;
  name?: string;
  role?: string;
  companyId?: string;
  lastSeenAt?: string;
  updatedAt?: string;
}

export interface AdminBroadcastUnsubscribe {
  id?: string;
  email: string;
  source?: string;
  unsubscribedAt?: string;
}

export interface AdminWorkspaceSnapshot {
  id?: string;
  scopeId: string;
  ownerEmail?: string;
  users?: Array<{ id?: string; email: string; name?: string; role?: string }>;
  appState?: {
    trucks?: any[];
    maintenance?: any[];
    parts?: any[];
  };
  fleetOpsWorkspace?: {
    inspections?: any[];
    issueReports?: any[];
    workOrders?: any[];
    complianceDocuments?: any[];
    telematicsEvents?: any[];
    teamMembers?: any[];
    auditEvents?: any[];
  };
  updatedAt?: string;
}

export interface AdminUserWorkspaceView {
  userId: string;
  account: BroadcastRecipient;
  profile?: AdminRegisteredUserProfile | null;
  workspaceScopeId: string;
  workspaceStatus: 'persisted' | 'shared' | 'not-initialized';
  workspaceExists: boolean;
  matchingAuditEvents?: any[];
  activity?: any[];
  snapshot: AdminWorkspaceSnapshot;
}

export interface BroadcastPayload {
  ownerEmail: string;
  subject: string;
  message: string;
  recipients?: BroadcastRecipient[];
}

export interface BroadcastFailure {
  email: string;
  error?: string;
  reason?: string;
  status?: number;
}

export interface BroadcastHistoryRecord {
  id: string;
  createdAt: string;
  subject: string;
  deliverableSubject?: string;
  campaignCode?: string;
  messagePreview?: string;
  requested: number;
  sent: number;
  failed: number;
  recipients?: string[];
  failures?: BroadcastFailure[];
}

export interface OwnerDirectoryData {
  users: BroadcastRecipient[];
  registeredUsers: AdminRegisteredUserProfile[];
  workspaceSnapshots: AdminWorkspaceSnapshot[];
  userWorkspaceViews: AdminUserWorkspaceView[];
  broadcastHistory: BroadcastHistoryRecord[];
  unsubscribes: AdminBroadcastUnsubscribe[];
}

class CommunicationService {
  private async getAuthHeaders(): Promise<HeadersInit> {
    const token = await auth.currentUser?.getIdToken().catch(() => null);
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  async sendFeedback(payload: FeedbackPayload): Promise<void> {
    const response = await fetch(getApiUrl('/api/feedback', process.env.REACT_APP_FEEDBACK_API_URL), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    const result = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(getApiErrorMessage(result, 'Feedback email could not be sent.'));
    }
  }

  async sendOwnerBroadcast(payload: BroadcastPayload): Promise<{ sent: number; failed: number; failures: BroadcastFailure[]; broadcastLog?: BroadcastHistoryRecord }> {
    const authHeaders = await this.getAuthHeaders();
    const response = await fetch(getApiUrl('/api/broadcast-email', process.env.REACT_APP_BROADCAST_API_URL), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify(payload)
    });

    const result = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(getApiErrorMessage(result, 'Broadcast email could not be sent.'));
    }

    return {
      sent: Number(result.sent || 0),
      failed: Number(result.failed || 0),
      failures: Array.isArray(result.failures) ? result.failures : [],
      broadcastLog: result.broadcastLog
    };
  }

  async listOwnerDirectoryData(ownerEmail: string): Promise<OwnerDirectoryData> {
    const authHeaders = await this.getAuthHeaders();
    const response = await fetch(getApiUrl('/api/admin-users', process.env.REACT_APP_ADMIN_USERS_API_URL), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ ownerEmail })
    });

    const result = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(getApiErrorMessage(result, 'Firebase Auth users could not be loaded.'));
    }

    const workspaceSnapshots: AdminWorkspaceSnapshot[] = Array.isArray(result.workspaceSnapshots) ? result.workspaceSnapshots : [];
    const userWorkspaceViews: AdminUserWorkspaceView[] = Array.isArray(result.userWorkspaceViews)
      ? result.userWorkspaceViews
        .map((view: Partial<AdminUserWorkspaceView>) => ({
          ...view,
          snapshot: view.snapshot || workspaceSnapshots.find(snapshot => snapshot.scopeId === view.workspaceScopeId)
        }))
        .filter((view: Partial<AdminUserWorkspaceView>) => Boolean(view.userId && view.account && view.workspaceScopeId && view.snapshot)) as AdminUserWorkspaceView[]
      : [];

    return {
      users: Array.isArray(result.users) ? result.users : [],
      registeredUsers: Array.isArray(result.registeredUsers) ? result.registeredUsers : [],
      workspaceSnapshots,
      userWorkspaceViews,
      broadcastHistory: Array.isArray(result.broadcastHistory) ? result.broadcastHistory : [],
      unsubscribes: Array.isArray(result.unsubscribes) ? result.unsubscribes : []
    };
  }

  async listRegisteredAuthUsers(ownerEmail: string): Promise<BroadcastRecipient[]> {
    const directory = await this.listOwnerDirectoryData(ownerEmail);
    return directory.users;
  }
}

export const communicationService = new CommunicationService();
export default communicationService;
